import React, { useState, useEffect } from "react";
import { useDocumentContext } from "../context/DocumentContext";
import { useNavigate } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import {
  ArrowLeft,
  Save,
  Scissors,
  Trash2,
  FileText,
  AlertTriangle,
  CheckCircle,
  Merge,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Layers,
} from "lucide-react";
import { getApiBase } from "../config";
import { getToken } from "../auth";

// Configure PDF Worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// --- CONFIGURATION ---
const API_BASE_URL = getApiBase();
// const API_BASE_URL = "http://localhost:8000";

// --- TYPES ---
interface ExtractionField {
  field_name: string;
  page_number: number;
  box_2d: [number, number, number, number];
}

interface SplitSegment {
  id: string;
  startPage: number;
  endPage: number;
  docType: string;
  extractionFields?: ExtractionField[];
  // Cloud-detected signing date (MIke), used to build the final_filename
  // in promissory_note mode so the edge worker doesn't have to re-extract.
  signingDate?: string | null;
}

export default function SplitterPage() {
  const navigate = useNavigate();

  // --- CONTEXT ---
  const { file, analysisData, tempRedactionPayload, setTempRedactionPayload, processMode } =
    useDocumentContext();

  // --- STATE ---
  const [numPages, setNumPages] = useState<number>(0);
  const [segments, setSegments] = useState<SplitSegment[]>([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(
    null,
  );
  const [namingMap, setNamingMap] = useState<Record<string, string>>({}); // For renaming
  const [isSaving, setIsSaving] = useState(false);

  // Preview State (Right Pane)
  const [previewPage, setPreviewPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);

  // --- EFFECT 1: INITIAL CHECKS & CONFIG ---
  useEffect(() => {
    if (!file) {
      navigate("/");
      return;
    }

    // Fetch the Abbreviation Map (Contract -> CTRT)
    fetch(`${API_BASE_URL}/config/abbreviations`)
      .then((res) => res.json())
      .then((data) => setNamingMap(data))
      .catch((err) => console.error("Failed to load naming conventions", err));
  }, [file, navigate]);

  // --- EFFECT 2: INITIALIZE SEGMENTS FROM AI ---
  useEffect(() => {
    if (analysisData?.document_analysis && segments.length === 0) {
      const aiSegments: SplitSegment[] = analysisData.document_analysis.map(
        (doc: any, index: number) => ({
          id: `ai-${index}`,
          startPage: Number(doc.start_page),
          endPage: Number(doc.end_page),
          docType: doc.document_type || "Exhibit",
          // From refined pnote segments: per-segment extraction_fields with the
          // signing-date box, page_number is local to this split.
          extractionFields: Array.isArray(doc.extraction_fields)
            ? doc.extraction_fields
            : undefined,
          signingDate: doc.signing_date ?? null,
        }),
      );

      if (aiSegments.length > 0) {
        setSegments(aiSegments);
        setSelectedSegmentId(aiSegments[0].id);
        setPreviewPage(aiSegments[0].startPage);
      } else {
        // Fallback: One big segment if AI failed to split
        setSegments([
          { id: "default", startPage: 1, endPage: 1, docType: "Unclassified" },
        ]);
      }
    }
  }, [analysisData]);

  // --- EFFECT 3: SYNC PREVIEW ---
  useEffect(() => {
    const seg = segments.find((s) => s.id === selectedSegmentId);
    if (seg) {
      if (previewPage < seg.startPage || previewPage > seg.endPage) {
        setPreviewPage(seg.startPage);
      }
    }
  }, [selectedSegmentId]);

  // --- ACTIONS ---

  const handleMergeDown = (index: number) => {
    if (index >= segments.length - 1) return;
    const current = segments[index];
    const next = segments[index + 1];

    const merged: SplitSegment = {
      ...current,
      endPage: next.endPage,
    };

    const newSegments = [...segments];
    newSegments.splice(index, 2, merged);
    setSegments(newSegments);
    setSelectedSegmentId(merged.id);
  };

  const handleSplitAtPage = () => {
    const segIndex = segments.findIndex((s) => s.id === selectedSegmentId);
    if (segIndex === -1) return;
    const seg = segments[segIndex];

    if (previewPage >= seg.endPage) {
      alert("Cannot split at the end of a document. Move to an earlier page.");
      return;
    }

    const newSegment1: SplitSegment = {
      ...seg,
      endPage: previewPage,
    };

    const newSegment2: SplitSegment = {
      id: `manual-${Date.now()}`,
      startPage: previewPage + 1,
      endPage: seg.endPage,
      docType: "New Document",
    };

    const newSegments = [...segments];
    newSegments.splice(segIndex, 1, newSegment1, newSegment2);
    setSegments(newSegments);
    setSelectedSegmentId(newSegment2.id);
    setPreviewPage(newSegment2.startPage);
  };

  const handleDeleteSegment = (id: string) => {
    if (
      !window.confirm(
        "Are you sure? This effectively deletes these pages from the final output.",
      )
    )
      return;
    setSegments((prev) => prev.filter((s) => s.id !== id));
    if (selectedSegmentId === id) setSelectedSegmentId(null);
  };

  // --- MASTER SAVE HANDLER ---
  const handleSaveJob = async () => {
    if (!file) return;
    setIsSaving(true);
    const token = getToken();
    try {
      // 1. Prepare Basics
      const hasRedactions =
        !!tempRedactionPayload && tempRedactionPayload.length > 0;
      const jobType = hasRedactions ? "SPLIT_AND_REDACT" : "SPLIT";

      // Clean original filename (remove extension & special chars)
      const cleanOriginalName = file.name
        .replace(/\.[^/.]+$/, "")
        .replace(/[^a-zA-Z0-9]/g, "_");

      // 2. Build Split Instructions with Intelligent Renaming
      // For promissory_note flow, the cloud has all the data MIke needs
      // (SSN from ADS manifest + signing_date) — we build the final filename
      // here and let the edge worker use it directly. No edge-side regex
      // extraction needed.
      const isPnote = processMode === "promissory_note";

      // Convert "04/12/06" / "April 12, 2006" / "12th day of April, 2006" → "04122006"
      // Returns "NODATE" if parsing fails. (Mirrors edge worker logic so cloud
      // and edge agree on the format.)
      const fmtSigningDate = (raw: string | null | undefined): string => {
        if (!raw || typeof raw !== "string") return "NODATE";
        const cleaned = raw
          .replace(/(?<=\d)(st|nd|rd|th)\b/gi, "")
          .replace(/\bday\s+of\s+/gi, "")
          .replace(/,/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        const parsed = new Date(cleaned);
        if (isNaN(parsed.getTime())) return "NODATE";
        let y = parsed.getFullYear();
        if (y < 100) y += y < 50 ? 2000 : 1900;
        if (y < 1990 || y > 2050) return "NODATE";
        const mm = String(parsed.getMonth() + 1).padStart(2, "0");
        const dd = String(parsed.getDate()).padStart(2, "0");
        return `${mm}${dd}${y}`;
      };

      const splitInstructions = segments.map((s) => {
        // Normalize input: "Contract" -> "contract"
        const userType = s.docType.trim();
        const lookupKey = userType.toLowerCase();

        let finalFilename: string;
        if (isPnote) {
          // Promissory note: "{digits-only-SSN}-{mmddyyyy}.pdf" — MIke date
          // comes through s.signingDate; we format it once here so the saved
          // template is self-contained and the edge doesn't need to re-extract.
          const ssnDigits = userType.replace(/[^0-9]/g, "");
          const dateValue = fmtSigningDate(s.signingDate);
          finalFilename = ssnDigits
            ? `${ssnDigits}-${dateValue}.pdf`
            : `PART_${dateValue}.pdf`;
        } else {
          // Generic split: "OriginalName_CTRT.pdf" using abbreviation map
          const docCode =
            namingMap[lookupKey] || userType.replace(/[^a-zA-Z0-9]/g, "_");
          finalFilename = `${cleanOriginalName}_${docCode}.pdf`;
        }

        return {
          page_start: s.startPage,
          page_end: s.endPage,
          final_filename: finalFilename,
          target_doc_type: userType, // Keep human readable name for metadata
          // Per-rule extraction_fields (e.g. SIGNING_DATE box) — kept for
          // future use; edge will prefer final_filename when present.
          extraction_fields: s.extractionFields || [],
        };
      });

      // 3. Construct Payload
      const payload = {
        document_name: file.name,

        // 👇 move these OUT of meta
        fingerprint_hash:
          analysisData?.meta?.fingerprint_hash ||
          analysisData?.meta?.fingerprint,
        fingerprint_version: 2,
        jurisdiction_code:
          analysisData?.jurisdiction_and_context?.jurisdiction_state ||
          "FEDERAL",

        payload: {
          splits: splitInstructions,
          redactions: tempRedactionPayload || [],
          fingerprint_profile: analysisData?.meta?.fingerprint_profile || null,
        },

        // meta can stay, backend will ignore it
        meta: {
          total_pages: numPages,
          case_id:
            analysisData?.jurisdiction_and_context?.case_id || "Unassigned",
        },
      };

      // 4. Send to Split Rules Endpoint (NEW)
      console.log("🚀 Saving Split Rules...", payload);

      const response = await fetch(`${API_BASE_URL}/process-job/split-rules`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errText = await response.text();
        console.error("Split save failed:", response.status, errText);
        throw new Error(errText || "Server rejected split save");
      }

      const result = await response.json();

      alert(`✅ Split Rules Saved! (Template ID: ${result.template_id})`);

      setTempRedactionPayload(null);
      navigate("/");
    } catch (err) {
      console.error("Failed to save split rules", err);
      alert("Failed to save split rules. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // --- RENDER ---
  const activeSegment = segments.find((s) => s.id === selectedSegmentId);

  if (!file) return <div className="p-10 text-white">Loading Splitter...</div>;

  return (
    <div className="h-screen flex flex-col bg-slate-50 dark:bg-slate-900 overflow-hidden font-sans">
      {/* HEADER */}
      <div className="h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-4 shrink-0 shadow-sm z-20">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-500 transition"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-bold text-slate-800 dark:text-white text-sm">
              Document Splitter
            </h1>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>{file.name}</span>
              {tempRedactionPayload && (
                <span className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded border border-emerald-200 dark:border-emerald-800 flex items-center gap-1">
                  <Layers className="w-3 h-3" /> Redactions Included
                </span>
              )}
            </div>
          </div>
        </div>

        <button
          onClick={handleSaveJob}
          disabled={isSaving}
          className={`flex items-center gap-2 px-6 py-2 rounded-lg font-bold text-sm shadow-lg transition-all
                ${
                  isSaving
                    ? "bg-slate-400 cursor-wait"
                    : "bg-blue-600 hover:bg-blue-500 text-white shadow-blue-500/20"
                }`}
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {isSaving
            ? "Processing..."
            : tempRedactionPayload
              ? "Confirm Redact & Split"
              : "Confirm Splits"}
        </button>
      </div>

      {/* MAIN WORKSPACE */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT SIDEBAR: SEGMENT LIST */}
        <div className="w-1/3 min-w-[300px] max-w-[400px] bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 flex flex-col z-10 shadow-xl">
          <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
              Detected Documents ({segments.length})
            </h3>
            <p className="text-[10px] text-slate-400">
              Drag or click to edit boundaries
            </p>
            {processMode === "promissory_note" && (() => {
              const missingCount = segments.filter((s) => !s.signingDate).length;
              if (missingCount === 0 && segments.length > 0) {
                return (
                  <div className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                    <CheckCircle className="w-3 h-3" />
                    All {segments.length} signing dates set
                  </div>
                );
              }
              return (
                <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400 font-medium">
                  <AlertTriangle className="w-3 h-3" />
                  {missingCount} of {segments.length} segment{segments.length === 1 ? "" : "s"} missing signing date
                </div>
              );
            })()}
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {segments.map((seg, index) => {
              const isSelected = selectedSegmentId === seg.id;

              const prevSeg = segments[index - 1];
              const hasGap = prevSeg && seg.startPage > prevSeg.endPage + 1;
              const gapSize = prevSeg ? seg.startPage - prevSeg.endPage - 1 : 0;

              return (
                <React.Fragment key={seg.id}>
                  {hasGap && (
                    <div className="flex items-center justify-center gap-2 py-2 text-xs text-amber-500 bg-amber-50 dark:bg-amber-900/20 rounded border border-dashed border-amber-300">
                      <AlertTriangle className="w-3 h-3" />
                      <span>{gapSize} Page(s) Skipped (Blank/Separator)</span>
                    </div>
                  )}

                  <div
                    onClick={() => {
                      setSelectedSegmentId(seg.id);
                      setPreviewPage(seg.startPage);
                    }}
                    className={`group relative p-4 rounded-xl border-2 transition-all cursor-pointer ${
                      isSelected
                        ? "bg-blue-50 dark:bg-blue-900/20 border-blue-500 shadow-md ring-1 ring-blue-200"
                        : "bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:border-blue-300 hover:shadow-sm"
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span
                          className={`flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${isSelected ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-600"}`}
                        >
                          {index + 1}
                        </span>
                        <input
                          type="text"
                          value={seg.docType}
                          placeholder="Document Type"
                          onChange={(e) => {
                            const newSegs = [...segments];
                            newSegs[index].docType = e.target.value;
                            setSegments(newSegs);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm font-bold bg-transparent border-b border-transparent hover:border-slate-300 focus:border-blue-500 focus:outline-none text-slate-800 dark:text-white w-40"
                        />
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          title="Delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSegment(seg.id);
                          }}
                          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-between items-center text-xs text-slate-500">
                      <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-900 px-2 py-1 rounded">
                        <FileText className="w-3 h-3" />
                        <span>
                          Pages {seg.startPage} - {seg.endPage}
                        </span>
                      </div>
                      <span className="font-mono text-slate-400">
                        {seg.endPage - seg.startPage + 1} pgs
                      </span>
                    </div>

                    {/* PROMISSORY NOTE: SIGNING DATE EDITOR */}
                    {processMode === "promissory_note" && (
                      <div className="mt-2 flex items-center gap-2 text-xs">
                        <span className="text-slate-500 dark:text-slate-400 font-medium">
                          Signing Date:
                        </span>
                        <input
                          type="text"
                          value={seg.signingDate || ""}
                          placeholder="MM/DD/YYYY"
                          onChange={(e) => {
                            const newSegs = [...segments];
                            newSegs[index].signingDate = e.target.value || null;
                            setSegments(newSegs);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className={`flex-1 font-mono text-xs px-2 py-1 rounded border focus:outline-none focus:ring-1 ${
                            seg.signingDate
                              ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700 text-emerald-900 dark:text-emerald-200 focus:ring-emerald-500"
                              : "bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200 focus:ring-amber-500"
                          }`}
                        />
                        {!seg.signingDate && (
                          <AlertTriangle
                            className="w-3 h-3 text-amber-500 flex-shrink-0"
                            aria-label="Missing signing date — file will be named with NODATE"
                          />
                        )}
                      </div>
                    )}

                    {/* MERGE BUTTON */}
                    {index < segments.length - 1 && isSelected && (
                      <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 z-20">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMergeDown(index);
                          }}
                          className="flex items-center gap-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 shadow-sm text-[10px] px-2 py-1 rounded-full text-slate-500 hover:text-blue-600 hover:border-blue-400 transition"
                          title="Merge with next document"
                        >
                          <Merge className="w-3 h-3" /> Merge
                        </button>
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* RIGHT PANE: INSPECTOR */}
        <div className="flex-1 bg-slate-100 dark:bg-slate-900/50 flex flex-col relative">
          {/* PAGINATION CONTROLS */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white/90 dark:bg-slate-800/90 backdrop-blur shadow-lg border border-slate-200 dark:border-slate-600 rounded-full px-4 py-2 flex items-center gap-4 z-20">
            <button
              disabled={
                !activeSegment || previewPage <= activeSegment.startPage
              }
              onClick={() => setPreviewPage((p) => p - 1)}
              className="p-1 hover:bg-slate-100 rounded-full disabled:opacity-30"
            >
              <ChevronLeft className="w-5 h-5 text-slate-700" />
            </button>

            <span className="text-sm font-mono font-bold text-slate-700 dark:text-white w-24 text-center">
              Page {previewPage}
            </span>

            <button
              disabled={!activeSegment || previewPage >= activeSegment.endPage}
              onClick={() => setPreviewPage((p) => p + 1)}
              className="p-1 hover:bg-slate-100 rounded-full disabled:opacity-30"
            >
              <ChevronRight className="w-5 h-5 text-slate-700" />
            </button>
          </div>

          {/* SPLIT BUTTON OVERLAY */}
          {activeSegment && previewPage < activeSegment.endPage && (
            <div className="absolute top-1/2 right-8 -translate-y-1/2 z-20 group">
              <button
                onClick={handleSplitAtPage}
                className="flex flex-col items-center gap-2 bg-white dark:bg-slate-800 p-3 rounded-xl shadow-xl border-2 border-dashed border-blue-400 text-blue-600 hover:bg-blue-50 transition transform hover:scale-105"
              >
                <Scissors className="w-6 h-6" />
                <span className="text-xs font-bold writing-vertical-lr">
                  Split Here
                </span>
              </button>
              <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none transition">
                Cut after Page {previewPage}
              </div>
            </div>
          )}

          {/* PDF PREVIEW */}
          <div className="flex-1 overflow-auto flex justify-center p-8 pt-20">
            {file && (
              <div className="relative shadow-2xl">
                <Document
                  file={file}
                  onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                >
                  <Page
                    pageNumber={previewPage}
                    scale={scale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="bg-white"
                  />
                </Document>

                <div className="absolute top-0 left-0 w-full h-2 bg-blue-500 z-10" />
                <div className="absolute top-2 right-2 bg-blue-600 text-white text-[10px] px-2 py-1 rounded opacity-50 hover:opacity-100">
                  Belongs to: {activeSegment?.docType}
                </div>
              </div>
            )}
          </div>

          <div className="absolute bottom-6 right-6 flex items-center gap-2 bg-white dark:bg-slate-800 p-1 rounded-lg shadow-md border border-slate-200">
            <button
              onClick={() => setScale((s) => Math.max(0.5, s - 0.1))}
              className="p-2 hover:bg-slate-100 rounded text-slate-600"
            >
              -
            </button>
            <span className="text-xs w-8 text-center">
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={() => setScale((s) => Math.min(2.0, s + 0.1))}
              className="p-2 hover:bg-slate-100 rounded text-slate-600"
            >
              +
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
